/**
 * Owner: Masa Bayu
 * File: ReaderProfilePublishing.tsx
 * Date:Time: 2026-10-29 18:40
 * Description: §513 — seksyen Penerbitan dalam SATU halaman profil. Semua angka
 *   datang daripada stor `GET /v1/publisher/dashboard` yang sama dipakai dashboard
 *   author (model dijana `buildAuthorDashboardModel` — tiada salinan logik pemformatan).
 *   Sesi reader menerima 403 daripada hub → paparan jadi satu klik naik taraf
 *   (`become-publisher`, Bearer yang sama, tanpa kata laluan), bukan borang log masuk.
 *   Seksyen tanpa sumber data (analitik, bayaran, storefront) TIDAK dipaparkan.
 */

import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { becomePublisher } from '../auth/hub-session';
import type { AuthSessionView } from '../auth/session-types';
import { useHubAuthState } from '../publication/hub-auth';
import { STUDIO_ENTRY } from '../author/author-production-identity';
import {
  refreshPublisherDashboard,
  resetPublisherDashboard,
  usePublisherDashboard,
} from '../author/private/publisher-dashboard';
import { buildAuthorDashboardModel } from '../author/private/private-author-view';
import { LandingIcon } from '../home/marketplace/landing/LandingIcons';
import type { ReaderProfileLabels } from './reader-profile-labels';

export interface ReaderProfilePublishingProps {
  readonly labels: ReaderProfileLabels;
  readonly interfaceTag: string;
  readonly onNavigate: (path: string) => void;
  readonly onSignedIn: (session: AuthSessionView) => void;
}

function formatDate(iso: string | null, interfaceTag: string): string {
  if (iso === null) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString(interfaceTag, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
}

export function ReaderProfilePublishing({
  labels,
  interfaceTag,
  onNavigate,
  onSignedIn,
}: ReaderProfilePublishingProps): ReactElement {
  const p = labels.publishing;
  const auth = useHubAuthState();
  const dash = usePublisherDashboard();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (auth !== 'signed-in') {
      resetPublisherDashboard();
      return;
    }
    void refreshPublisherDashboard();
  }, [auth]);

  const model = useMemo(
    () => buildAuthorDashboardModel(dash.view, p.priceUnknown, interfaceTag),
    [dash.view, interfaceTag, p.priceUnknown],
  );

  const upgrade = useCallback((): void => {
    setPending(true);
    setError(null);
    void becomePublisher()
      .then((session) => {
        onSignedIn(session);
        resetPublisherDashboard();
        return refreshPublisherDashboard({ force: true });
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : String(caught));
      })
      .finally(() => {
        setPending(false);
      });
  }, [onSignedIn]);

  if (dash.status === 'error') {
    return (
      <div className="qxr__stack">
        <section className="qxr__card" aria-labelledby="qxr-pub-error">
          <h2 id="qxr-pub-error" className="qxr__section-title">
            <LandingIcon name="pen" size={18} /> {p.heading}
          </h2>
          <p className="qxr__muted">{dash.error ?? '—'}</p>
          <div className="qxr__actions">
            <button
              type="button"
              className="qxr__cta"
              onClick={() => {
                void refreshPublisherDashboard({ force: true });
              }}
              data-publishing-retry=""
            >
              {p.retry}
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (dash.status === 'forbidden') {
    return (
      <div className="qxr__stack">
        <section className="qxr__card" aria-labelledby="qxr-pub-gate">
          <h2 id="qxr-pub-gate" className="qxr__section-title">
            <LandingIcon name="pen" size={18} /> {p.notPublisherTitle}
          </h2>
          <p className="qxr__muted">{p.notPublisherBody}</p>
          <div className="qxr__actions">
            <button
              type="button"
              className="qxr__cta"
              disabled={pending}
              onClick={upgrade}
              data-publishing-upgrade=""
            >
              <LandingIcon name="pen" size={15} /> {pending ? p.becoming : p.becomeCta}
            </button>
          </div>
          {error !== null ? <p className="qxr__danger">{error}</p> : null}
        </section>
      </div>
    );
  }

  if (dash.status !== 'ready') {
    return (
      <div className="qxr__stack">
        <section className="qxr__card" aria-busy="true" aria-labelledby="qxr-pub-loading">
          <h2 id="qxr-pub-loading" className="qxr__section-title">
            <LandingIcon name="pen" size={18} /> {p.heading}
          </h2>
          <p className="qxr__empty">{p.loading}</p>
        </section>
      </div>
    );
  }

  const kpis: readonly { readonly label: string; readonly value: string }[] = [
    { label: p.kpiBooks, value: String(model.booksPublished) },
    { label: p.kpiPaidOrders, value: String(model.paidOrders) },
    { label: p.kpiPending, value: String(model.pendingOrders) },
    { label: p.kpiBuyers, value: String(model.buyers) },
    { label: p.kpiRevenue, value: model.revenueText },
  ];

  return (
    <div className="qxr__stack">
      <section className="qxr__card" aria-labelledby="qxr-pub-kpi">
        <div className="qxr__card-head">
          <h2 id="qxr-pub-kpi" className="qxr__section-title">
            <LandingIcon name="pen" size={18} /> {p.heading}
          </h2>
          <button
            type="button"
            className="qxr__cta qxr__cta--ghost"
            onClick={() => {
              onNavigate(STUDIO_ENTRY);
            }}
            data-publishing-studio=""
          >
            <LandingIcon name="upload" size={15} /> {p.studioCta}
            <LandingIcon name="chevronRight" size={12} />
          </button>
        </div>
        <dl className="qxr__kpis">
          {kpis.map((k) => (
            <div key={k.label} className="qxr__kpi">
              <dt>{k.label}</dt>
              <dd>{k.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="qxr__card" aria-labelledby="qxr-pub-books">
        <h2 id="qxr-pub-books" className="qxr__section-title">
          <LandingIcon name="book" size={18} /> {p.booksHeading}
        </h2>
        {model.books.length === 0 ? (
          <p className="qxr__empty">{p.emptyBooks}</p>
        ) : (
          <ul className="qxr__rows">
            {model.books.map((book) => (
              <li key={book.publicationId}>
                <span className="qxr__rowtitle">{book.title}</span>
                <span className="qxr__rowmeta">
                  {`${p.colRevision} ${book.revision === null ? '—' : String(book.revision)} · ${
                    book.priceText
                  } · ${p.colSold} ${String(book.soldOrders)} · ${book.revenueText} · ${
                    book.listed ? p.listed : p.unlisted
                  }`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="qxr__card" aria-labelledby="qxr-pub-orders">
        <h2 id="qxr-pub-orders" className="qxr__section-title">
          <LandingIcon name="receipt" size={18} /> {p.ordersHeading}
        </h2>
        {model.orders.length === 0 ? (
          <p className="qxr__empty">{p.emptyOrders}</p>
        ) : (
          <ul className="qxr__rows">
            {model.orders.map((order) => (
              <li key={order.orderId}>
                <span className="qxr__rowtitle">{order.title}</span>
                <span className="qxr__rowmeta">
                  {`${order.state} · ${order.amountText} · ${formatDate(order.createdAt, interfaceTag)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
